<template>
    <div>
        <v-row class="white-card px-0">
            <v-col cols="12">
                <toolbar-stepper :step="carousel" :items="stepperTabs"/>
            </v-col>
            <v-col cols="12" class="pb-9">
                <!-- CAROUSEL -->
                <v-carousel :show-arrows="false" light hide-delimiters touchless v-model="carousel" height="115%">
                    <!-- BASIC INFO -->
                    <v-carousel-item>
                        <routine-name
                            :defaults="routineNameInfo"
                            @change="onNameChange"
                            @img="onImgChange"
                            ref="routineName"
                        />
                    </v-carousel-item>
                    <v-carousel-item>
                        <div class="title-section mb-6">
                            {{ $t('routines.add_blocks_and_exercises') }}
                        </div>
                        <routine-blocks
                            class="px-4"
                            ref="blocksManager"
                            @close:exercises-dialog="onExercisesDialogClose"
                            :default-blocks="blocks"
                            @change="blocks = $event"/>
                    </v-carousel-item>
                    <v-carousel-item>
                        <div class="title-section mb-3">
                            {{ $t('routines.assign_to_calendar') }}
                        </div>
                        <assign-routine-selector @success="toRoutines" :routine="routinePayload" @valid="assignValid = $event"
                                                 @loading="loading = $event" ref="assignRoutine"/>
                    </v-carousel-item>
                </v-carousel>
            </v-col>
            <v-col cols="6">
                <v-btn class="cancel-btn" @click="goBack">
                    {{ cancelLabel }}
                </v-btn>
            </v-col>
            <v-col cols="6" class="text-right">
                <v-btn class="next-btn" @click="continueStepper" :loading="loading">
                    {{ nextLabel }}
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>

import RoutineName from "@/components/coach/routines/RoutineName";
import RoutineObjective from "@/components/coach/routines/RoutineObjective";
import RoutineBlocks from "@/components/coach/routines/RoutineBlocks";

import {db, Timestamp} from '@/utils/Firebase'

import _map from 'lodash/map'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import _compact from 'lodash/compact'

import {sumDuration} from "@/utils/Exercises";
import ToolbarStepper from "@/components/elements/ToolbarStepper";
import AssignRoutineSelector from "@/components/coach/routines/AssignRoutineSelector";

export default {
    name: "RoutineCreator",
    components: {
        AssignRoutineSelector,
        ToolbarStepper,
        RoutineName,
        RoutineObjective,
        RoutineBlocks,
    },
    props: {
        defaultRoutine: {
            type: Object
        },
        hideUseExisting: {
            type: Boolean,
            default: false
        }
    },
    data(vm) {
        return {
            routineID: null,
            loading: false,
            carousel: 0,
            name: '',
            uploadedImg: null,
            imgUrl: '',
            objective: '',
            blocks: [{exercises: []}],
            currentError: '',
            assignValid: false,

            stepperTabs: [
                vm.$t('steppers.details'),
                vm.$t('steppers.blocks_and_exercises'),
                vm.$t('steppers.assign'),
            ]
        }
    },
    computed: {
        routineNameInfo(vm = this) {
            return {
                name: vm.name,
                img: vm.imgUrl,
                objective: vm.objective
            }
        },
        cancelLabel(vm = this) {
            if (vm.carousel === 1) return vm.$t('common.back_to_details')
            if (vm.carousel === 2) return vm.$t('common.to_routines')
            return vm.$t('common.cancel')
        },
        nextLabel(vm = this) {
            if (vm.carousel === 1) return vm.$t('common.save')
            if (vm.carousel === 2) return vm.$t('common.assign')
            return vm.$t('common.next')
        },
        isValid(vm = this) {
            vm.currentError = ''
            if (vm.carousel === 0) {
                if (!vm.name) {
                    vm.currentError = vm.$t('routines.routine_name_required')
                    return false
                }
                if (!vm.objective) {
                    vm.currentError = vm.$t('routines.routine_objective_required')
                    return false
                }
            }

            if (vm.carousel === 1) {
                if (vm.blocks.length === 0) {
                    vm.currentError = vm.$t('routines.routine_blocks_required')
                    return false
                }
                let blockValid = true

                vm.blocks.forEach(block => {
                    if (block.exercises.length === 0) {
                        vm.currentError = vm.$t('routines.routine_exercises_required')
                        return blockValid = false
                    }

                    if (block.exercises[0].type === 'rest') {
                        vm.currentError = 'No se puede agregar un descanso al comienzo'
                        return blockValid = false
                    }

                    let qtyExerciseWithConfig = 0
                    let qtyRestContinuos = 0
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest' && exercise.config && exercise.config.sets.length > 0) qtyExerciseWithConfig++
                        if (exercise.type === 'rest') {
                            qtyRestContinuos++
                            if (qtyRestContinuos === 2) {
                                vm.currentError = 'No se puede tener 2 descansos seguidos'
                                return blockValid = false
                            }
                        } else {
                            qtyRestContinuos = 0
                        }
                    })

                    if (qtyExerciseWithConfig !== block.exercises.filter(ex => ex.type !== 'rest').length) {
                        vm.currentError = vm.$t('routines.routine_sets_required')
                        return blockValid = false
                    }
                })

                return blockValid
            }

            if (vm.carousel === 2) {
                if(!vm.assignValid) {
                    vm.currentError = vm.$t('routines.routine_assign_error')
                    return false
                }
            }

            vm.currentError = ''
            return true
        },
        routinePayload(vm = this) {
            const exercises = _flatten(_map(vm.blocks, 'exercises'))
            let equipments = []
            let duration = 0
            let unit = vm.$tc('common.seconds', 2)

            if (exercises && exercises.length > 0) {
                const durationCalc = sumDuration(exercises)
                equipments = _uniq(_compact(_flatten(_map(exercises, 'equipments'))))
                duration = durationCalc.duration
                unit = durationCalc.unit
            }

            return {
                id: vm.defaultRoutine?.id || vm.defaultRoutine?.uid || vm.routineID || '',
                coachId: vm.currentUser.uid,
                owner: vm.account && vm.account.appTeam ? 'app' : 'user',
                name: vm.name || '',
                img: vm.uploadedImg?.url || '',
                imgSavePath: vm.uploadedImg?.savePath || '',
                description: {
                    equipments: equipments,
                    blocks: vm.blocks.length,
                    objective: vm.objective || '',
                    duration: duration,
                    unit: unit
                },
                blocks: vm.blocks && vm.blocks.map((block, i) => {
                    let blockDuration = sumDuration(block.exercises)
                    return {
                        id: Number(i) + 1,
                        duration: blockDuration.duration,
                        unit: blockDuration.unit,
                        exercises: block.exercises && block.exercises.map(exercise => {
                            return {
                                name: exercise.name || '',
                                type: exercise.type || 'exercise',
                                time: exercise.time || '',
                                hasTime: !!exercise.time,
                                objectID: exercise.id || exercise.objectID || '',
                                config: exercise.config || null,
                                id: exercise.id || exercise.objectID || '', //todo remove id after testing components compatibility
                            }
                        }) || []
                    }
                }) || []
            }
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        onNameChange(data) {
            let vm = this
            vm.name = data.name || ''
            vm.imgName = data.imgName || ''
            vm.imgUrl = data.imgUrl || ''
            vm.objective = data.objective || ''
        },
        onImgChange(img) {
            const vm = this
            vm.uploadedImg = img || null
        },
        setDefaults() {
            const vm = this
            if (!vm.defaultRoutine) return;
            vm.name = vm.defaultRoutine.name || ''
            vm.imgUrl = vm.defaultRoutine.img || ''
            vm.objective = vm.defaultRoutine?.description?.objective || ''
            vm.blocks = vm.defaultRoutine.blocks || []
        },
        reset() {
            const vm = this
            vm.carousel = 0
            vm.name = ''
            vm.uploadedImg = null
            vm.objective = ''
            vm.blocks = []
        },
        onExercisesDialogClose() {
            const vm = this
            vm.$nextTick(function () {
                vm.setOnBackPress()
            })
        },
        setOnBackPress() {
            const vm = this
            vm.onBackPress(vm.goBack)
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.$router.back()
            if (vm.carousel === 2) return vm.toRoutines()
            vm.carousel--
        },
        continueStepper() {
            const vm = this
            if (!vm.isValid) return vm.toast(vm.currentError, 'error')
            if (vm.carousel === 1) return vm.save()
            if (vm.carousel === 2) return vm.$refs.assignRoutine.checkAssignedRoutines()
            return vm.carousel++
        },
        async save() {
            const vm = this

            if (!vm.isValid) return vm.toast(vm.currentError, 'error')

            vm.loading = true

            await vm.$refs.routineName.upload()
            let payload = {
                ...vm.routinePayload,
                created_at: Timestamp.now(),
            }
            delete payload.id

            console.log('routine payload', payload)

            if (vm.defaultRoutine || vm.routineID) {
                await db.collection('routines')
                    .doc(vm.defaultRoutine?.id || vm.defaultRoutine?.uid || vm.routineID)
                    .update(payload)
            } else {
                let ref = await db.collection('routines')
                    .add(payload)
                vm.routineID = ref.id
            }

            vm.loading = false
            vm.$emit('success', payload)
            vm.carousel++
        },
        toRoutines() {
            const vm = this
            vm.$router.push({name: `${vm.accountType}.routines`})
        }
    },
    watch: {
        carousel(val) {
            const vm = this
            window.scrollTo(0, 0)
            val > 0 ? vm.setOnBackPress() : vm.clearOnBack()
        },
    },
}
</script>

<style scoped>

.title-section {
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
    color: #333333;
    text-align: center;
    margin-top: 20px;
}

.cancel-btn {
    border: 1px solid #F9F9F9;
    border-radius: 8px;
    color: #797979;
    text-transform: none;
    box-shadow: none;
    background: transparent !important;
}

.next-btn {
    border: 1px solid #F9F9F9;
    border-radius: 8px;
    color: #000000;
    text-transform: none;
    box-shadow: none;
    background: #CCFF99 !important;
}

</style>